import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import { whatsappLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  "Retina Consultation",
  "Cataract Consultation",
  "Diabetic Eye Check-up",
  "General Eye Check-up",
  "Paediatric Eye Care",
  "Second Opinion",
  "Other",
];

const TIME_SLOTS = ["9:00 AM – 12:00 PM", "12:00 PM – 4:00 PM", "4:00 PM – 9:00 PM"];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-ink placeholder:text-slate-400 placeholder:font-normal focus:border-brand-bright focus:ring-2 focus:ring-brand-bright/20 outline-none transition";

const Field = ({ label, children, id }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-ink/70 mb-2">
      {label}
    </label>
    {children}
  </div>
);

export const AppointmentForm = () => {
  const [form, setForm] = useState({
    patient_name: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    reason: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success("Appointment request received. Our team will call you to confirm.");
      const waText = `Hello iNetra, I would like to book an appointment.\n\nName: ${form.patient_name}\nPhone: ${form.phone}\nPreferred Date: ${form.preferred_date}\nPreferred Time: ${form.preferred_time}\nReason: ${form.reason}${form.message ? `\nMessage: ${form.message}` : ""}`;
      window.open(whatsappLink(waText), "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error("Could not submit right now. Please call or WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center" data-testid="appointment-success">
        <CalendarCheck className="w-10 h-10 text-emerald-600 mx-auto mb-4" aria-hidden="true" />
        <h3 className="text-xl font-extrabold text-ink">Request received, {form.patient_name.split(" ")[0]}.</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Thank you for choosing iNetra. Our team will call you on <strong>{form.phone}</strong> shortly to confirm
          your appointment for <strong>{form.preferred_date}</strong> ({form.preferred_time}). WhatsApp has been opened
          with your details so you can also reach us instantly.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ patient_name: "", phone: "", preferred_date: "", preferred_time: "", reason: "", message: "" });
          }}
          className="btn-outline mt-6 !text-[13px]"
          data-testid="book-another-button"
        >
          Book another appointment
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-5" data-testid="appointment-form" noValidate={false}>
      <Field label="Patient Name" id="appt-name">
        <input id="appt-name" required value={form.patient_name} onChange={set("patient_name")} className={inputCls} placeholder="Full name" data-testid="appointment-name-input" autoComplete="name" />
      </Field>
      <Field label="Phone Number" id="appt-phone">
        <input id="appt-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="10-digit mobile number" data-testid="appointment-phone-input" autoComplete="tel" />
      </Field>
      <Field label="Preferred Date" id="appt-date">
        <input id="appt-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="appointment-date-input" min={new Date().toISOString().split("T")[0]} />
      </Field>
      <Field label="Preferred Time" id="appt-time">
        <select id="appt-time" required value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} data-testid="appointment-time-select">
          <option value="" disabled>Select a time slot</option>
          {TIME_SLOTS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Reason for Visit" id="appt-reason">
          <select id="appt-reason" required value={form.reason} onChange={set("reason")} className={inputCls} data-testid="appointment-reason-select">
            <option value="" disabled>Select a reason</option>
            {REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Message (optional)" id="appt-message">
          <textarea id="appt-message" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Anything you'd like us to know before your visit" data-testid="appointment-message-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={submitting} className="btn-primary w-full !py-4 !text-base disabled:opacity-70 disabled:cursor-not-allowed" data-testid="appointment-submit-button">
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Sending request…
            </>
          ) : (
            <>
              <CalendarCheck className="w-5 h-5" aria-hidden="true" /> Request Appointment
            </>
          )}
        </button>
        <p className="mt-3 text-center text-xs text-slate-500">
          No advance payment. Our team confirms every appointment personally by phone.
        </p>
      </div>
    </form>
  );
};
